/** tm/components/ConsultDetailModal.tsx — 상담 상세 조회 모달 (PeekStack 지원) */
import { useState, useEffect } from 'react';
import type { Consultation, Task, TaskStep, FP, WM, TaskTemplate, TaskCategory, AIPreset, UploadedFile } from '../types';
import { C, T, R, Badge } from './ui';
import * as api from '../api';
import ConsultResultModal from './ConsultResultModal';
import ConsultRecordings from './ConsultRecordings';
import ConsultEditModal from './ConsultEditModal';
import RenameFileList from './RenameFileList';
import SendResultPanel from './SendResultPanel';
import { consultWmIds, wmNames } from '../wmList';

function d10(d: string | null | undefined): string { if (!d) return ''; return d.slice(0, 10); }

const STEP_TYPES: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  work: { label: '일반 작업', icon: '⚙️', color: '#6B7280', bg: '#F3F4F6' },
  collect: { label: '자료수집', icon: '📂', color: '#2563EB', bg: '#EFF6FF' },
  ai_questionnaire: { label: 'AI 질문지', icon: '🤖', color: '#7C3AED', bg: '#F3E8FF' },
  ai_bizplan: { label: 'AI 사업계획서', icon: '📊', color: '#7C3AED', bg: '#F3E8FF' },
  confirm: { label: '확인', icon: '✅', color: '#059669', bg: '#ECFDF5' },
  contract: { label: '계약', icon: '📝', color: '#D97706', bg: '#FFFBEB' },
  meeting: { label: '미팅', icon: '🤝', color: '#2563EB', bg: '#EFF6FF' },
  report: { label: '보고서', icon: '📋', color: '#6B7280', bg: '#F3F4F6' },
  invoice: { label: '청구', icon: '💰', color: '#D97706', bg: '#FFFBEB' },
};
function stepTypeInfo(t: string) { return STEP_TYPES[t] || STEP_TYPES.work; }
const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  '대기': { color: '#6B7280', bg: '#F3F4F6' },
  '진행': { color: '#2563EB', bg: '#EFF6FF' },
  '완료': { color: '#059669', bg: '#ECFDF5' },
};

const secH: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: '#1B3A5C', paddingBottom: 8, borderBottom: '2px solid #E5E7EB', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 };

export const MODAL_HEADER: React.CSSProperties = {
  padding: '14px 20px',
  background: 'linear-gradient(135deg, #111827, #1B3A5C)',
  borderRadius: '12px 12px 0 0',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};
export const MODAL_FOOTER: React.CSSProperties = {
  padding: '12px 20px',
  borderTop: `1px solid #E5E7EB`,
  display: 'flex', gap: 8,
  background: '#F8FAFC',
  borderRadius: '0 0 12px 12px',
};

interface Props {
  consultation: Consultation;
  tasks?: Task[];
  fps?: FP[];
  wms?: WM[];
  templates?: TaskTemplate[];
  taskCategories?: TaskCategory[];
  aiPresets?: AIPreset[];
  onDone: () => void;
  onCancel: () => void;
  onToast?: (msg: string, type?: 'success' | 'error') => void;
  panelMode?: boolean;
  onPushResult?: () => void;
  /** 이 화면에서 상담을 고쳤다(파일 이름 등) — 닫을 때 목록을 다시 읽게 */
  onChanged?: () => void;
}

export default function ConsultDetailModal({
  consultation: c, tasks: propTasks, fps = [], wms = [],
  templates = [], taskCategories = [], aiPresets = [],
  onDone, onCancel, onToast,
  panelMode, onPushResult, onChanged,
}: Props) {
  const [tasks, setTasks] = useState<Task[]>(propTasks || []);
  const [sendOpen, setSendOpen] = useState(false);   // 결과 보내기(담당 FP · 함께한 WM — SendResultPanel · 2026-10-27)
  const [showResultModal, setShowResultModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [appointmentCollapsed, setAppointmentCollapsed] = useState(false);
  // 파일 이름 고친 것 — 화면에 바로(부모 목록은 닫을 때 다시 읽음) · 상담이 바뀌면 비움 (2026-10-18)
  const [filesOv, setFilesOv] = useState<UploadedFile[] | null>(null);
  const [refOv, setRefOv] = useState<UploadedFile[] | null>(null);
  const [changed, setChanged] = useState(false);
  useEffect(() => { setFilesOv(null); setRefOv(null); setChanged(false); }, [c.id]);
  const close = () => (changed ? onDone() : onCancel());

  const hasResult = c.status === 'completed' || c.status === 'sent' || !!(c.content && c.content.trim());

  useEffect(() => { if (hasResult) setAppointmentCollapsed(true); }, [hasResult]);

  useEffect(() => {
    if (propTasks && propTasks.length > 0) { setTasks(propTasks); return; }
    if (!c.company_id) return;
    const token = localStorage.getItem('token') || '';
    fetch(`/api/tm/tasks?company_id=${c.company_id}`, { headers: { Authorization: 'Bearer ' + token } })
      .then(r => r.json())
      .then(json => setTasks((json.data || json || []).filter((t: Task) => t.consult_id === c.id)))
      .catch(() => {});
  }, [c.id, c.company_id, propTasks]);

  async function reloadTasks() {
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/tasks?company_id=${c.company_id}`, { headers: { Authorization: 'Bearer ' + token } });
      const json = await res.json();
      setTasks((json.data || json || []).filter((t: Task) => t.consult_id === c.id));
    } catch { /* ignore */ }
  }

  const fileDownload = async (f: { name: string; s3_key?: string }) => {
    if (!f.s3_key) return;
    try {
      const token = localStorage.getItem('token') || '';
      const res = await fetch(`/api/tm/download?key=${encodeURIComponent(f.s3_key)}&fileName=${encodeURIComponent(f.name || '')}`, { headers: { Authorization: 'Bearer ' + token } });
      const json = await res.json();
      if (json.data?.url) window.open(json.data.url, '_blank');
    } catch { /* */ }
  };

  const fpName = c.fp_name || fps.find(f => f.id === c.fp_id)?.name || '-';
  const fpCompany = c.fp_company || fps.find(f => f.id === c.fp_id)?.company || '';
  const wmName = wmNames(consultWmIds(c), wms, c.wm_names || (c.wm_name ? [c.wm_name] : [])) || '-';   // 함께한 WM 여러 명(2026-10-26)
  const feeLabel = c.fee_type === 'free' ? '면제' : `${(c.fee_amount || 0).toLocaleString()}원`;
  const confirmLabel = c.confirm_status === 'tentative' ? '미정' : '확정';
  const govTags: string[] = (() => { const raw = (c as unknown as Record<string, unknown>).gov_tags; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
  const parsedRefFiles: UploadedFile[] = (() => { const raw = c.ref_files; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { const p = JSON.parse(raw); return Array.isArray(p) ? p : []; } catch { return []; } } return []; })();
  const resultFiles: UploadedFile[] = filesOv || (Array.isArray(c.files) ? c.files : []);
  const refFiles: UploadedFile[] = refOv || parsedRefFiles;
  /** 첨부 파일 이름 고치기 — 상담의 files · ref_files 칸을 통째로 저장(s3 키는 그대로 · 받는 이름만 바뀜) */
  async function renameFile(kind: 'files' | 'ref_files', index: number, name: string) {
    const cur = kind === 'files' ? resultFiles : refFiles;
    const next = cur.map((f, k) => (k === index ? { ...f, name } : f));
    try {
      await api.updateConsultation(c.id, kind === 'files' ? { files: next } : { ref_files: next });
      if (kind === 'files') setFilesOv(next); else setRefOv(next);
      setChanged(true); if (onChanged) onChanged();
      if (onToast) onToast('파일 이름을 고쳤어요');
    } catch (e) {
      if (onToast) onToast(e instanceof Error ? e.message : '파일 이름을 저장하지 못했어요', 'error');
      throw e;
    }
  }

  async function handleResultDone() {
    setShowResultModal(false);
    await reloadTasks();
    onDone();
  }

  function handleOpenResult() {
    if (panelMode && onPushResult) {
      onPushResult();
    } else {
      setShowResultModal(true);
    }
  }

  const panelContent = (
    <>
      {showEditModal && (
        <ConsultEditModal
          consultation={c} wms={wms} fps={fps}
          onDone={() => { setShowEditModal(false); onDone(); }}
          onCancel={() => setShowEditModal(false)}
          onToast={msg => { if (onToast) onToast(msg); }}
        />
      )}

      <div style={panelMode ? { ...MODAL_HEADER, borderRadius: 0 } : MODAL_HEADER}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name || '상담 상세'}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
            {d10(c.date)} {c.time || ''}{c.location ? ` · ${c.location}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: T.xs, padding: '3px 10px', borderRadius: 10, fontWeight: 600,
            background: hasResult ? '#ECFDF5' : '#FFFBEB', color: hasResult ? '#059669' : '#D97706',
          }}>{hasResult ? '결과 입력 완료' : '결과 미입력'}</span>
          {!panelMode && (
            <button onClick={close} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: T.sm, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
        <div style={{ ...secH, cursor: hasResult ? 'pointer' : 'default', userSelect: 'none' }}
          onClick={() => { if (hasResult) setAppointmentCollapsed(!appointmentCollapsed); }}>
          <span>📋 약속 정보</span>
          <button onClick={(e) => { e.stopPropagation(); setShowEditModal(true); }}
            style={{ marginLeft: 'auto', fontSize: T.xs, padding: '3px 10px', borderRadius: 6, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, cursor: 'pointer', fontWeight: 600 }}>✏️ 수정</button>
          {hasResult && <span style={{ fontSize: T.xs, color: C.muted, marginLeft: 6 }}>{appointmentCollapsed ? '▶' : '▼'}</span>}
        </div>
        {!appointmentCollapsed && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#F8FAFC' }}>
              <InfoCell label="FP" value={c.fp_id ? `${fpName}${fpCompany ? ` (${fpCompany})` : ''}` : 'FP 없음 — WM 님이 FP 역할'} />
              <InfoCell label="WM" value={wmName} />
              <InfoCell label="상담료" value={feeLabel} />
              <InfoCell label="확정여부" value={confirmLabel} />
            </div>
            <div style={{ marginTop: 10, padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff' }}>
              <div style={{ marginBottom: c.notes || parsedRefFiles.length > 0 ? 10 : 0 }}>
                <div style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginBottom: 3 }}>상담 주제</div>
                <div style={{ fontSize: T.sm, fontWeight: 500, color: c.topic ? C.text : C.muted, lineHeight: 1.5 }}>{c.topic || '(미입력)'}</div>
              </div>
              <div style={{ marginBottom: parsedRefFiles.length > 0 ? 10 : 0 }}>
                <div style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginBottom: 3 }}>사전 메모</div>
                {c.notes ? (
                  <div style={{ padding: '8px 12px', borderRadius: 6, background: '#F8FAFC', fontSize: T.sm, lineHeight: 1.7, color: C.sub, whiteSpace: 'pre-wrap' }}>{c.notes}</div>
                ) : (
                  <div style={{ fontSize: T.sm, color: C.muted }}>-</div>
                )}
              </div>
              {refFiles.length > 0 && (
                <div>
                  <div style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginBottom: 4 }}>첨부 파일</div>
                  <RenameFileList plain files={refFiles} onDownload={f => void fileDownload(f)} onRename={(i, n) => renameFile('ref_files', i, n)} />
                </div>
              )}
            </div>
          </div>
        )}
        {appointmentCollapsed && <div style={{ marginBottom: 20 }} />}

        <div style={secH}>🤝 상담 결과</div>
        <div style={{ marginBottom: 20 }}>
          {!hasResult ? (
            <div>
              <div style={{ padding: '16px 20px', borderRadius: 10, background: '#FFFBEB', border: '1px solid #F59E0B33', textAlign: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: T.md, color: '#D97706', fontWeight: 600 }}>아직 결과가 입력되지 않았습니다</div>
              </div>
              <button onClick={handleOpenResult}
                style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontSize: T.md, fontWeight: 700, cursor: 'pointer' }}>
                ✏️ 결과 입력
              </button>
            </div>
          ) : (
            <div>
              <div style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: T.sm, lineHeight: 1.7, whiteSpace: 'pre-wrap', minHeight: 40, color: C.text, marginBottom: 12 }}>{c.content || '(내용 없음)'}</div>
              {resultFiles.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 4 }}>첨부 파일</div>
                  <RenameFileList files={resultFiles} onDownload={f => void fileDownload(f)} onRename={(i, n) => renameFile('files', i, n)} />
                </div>
              )}
              {govTags.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 4 }}>정부지원사업 매칭</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {govTags.map((t: string) => (
                      <span key={t} style={{ fontSize: T.xs, padding: '2px 8px', borderRadius: 10, background: '#F3E8FF', color: '#7C3AED', border: '1px solid #7C3AED33' }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {tasks.length > 0 && (
                <div>
                  <div style={{ fontSize: T.xs, color: C.muted, marginBottom: 6 }}>진행 업무</div>
                  {tasks.map(task => (
                    <div key={task.id} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#F8FAFC', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>{task.icon || '📋'}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: T.sm, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.type || '업무'}</span>
                      {task.venture_project_id ? <span title="벤처인증 화면의 조합과 같은 업무 — 단계가 같이 움직여요" style={{ fontSize: T.xs, color: C.muted, flexShrink: 0 }}>🚀 벤처인증 연결</span> : null}
                      <span style={{ fontSize: T.xs, padding: '2px 8px', borderRadius: R.sm, fontWeight: 600,
                        background: task.status === 'FP요청대기' ? '#F3E8FF' : task.status === '완료' ? '#ECFDF5' : '#EFF6FF',
                        color: task.status === 'FP요청대기' ? '#7C3AED' : task.status === '완료' ? '#059669' : C.accent,
                      }}>{task.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {/* 녹음 · 화자별 대화 글(2026-10-28 지도사 「클로바노트처럼 · 발언 클릭하면 재생」) — FP · WM 포털에도 같이 */}
        <div style={secH}>🎙 녹음 · 대화 글</div>
        <div style={{ marginBottom: 20 }}><ConsultRecordings consultId={c.id} onToast={onToast} /></div>
      </div>

      {sendOpen && <SendResultPanel consultId={c.id} onClose={() => setSendOpen(false)} onToast={onToast} />}

      <div style={panelMode ? { ...MODAL_FOOTER, borderRadius: 0 } : MODAL_FOOTER}>
        <button onClick={close} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: T.sm, cursor: 'pointer' }}>닫기</button>
        <div style={{ flex: 1 }} />
        {/* 결과 보내기 — 담당 FP · 함께한 WM 님들 골라서 한 번에(2026-10-27 지도사 「WM 님들에게 전송하는 버튼도 · 동시에 보내거나」) */}
        {hasResult && (
          <button onClick={() => setSendOpen(v => !v)} aria-expanded={sendOpen}
            style={{ padding: '8px 16px', borderRadius: R.md, border: `1px solid ${C.accent}`, background: C.accentLt, color: C.accent, fontWeight: 700, fontSize: T.sm, cursor: 'pointer' }}>결과 보내기</button>
        )}
        <button onClick={handleOpenResult} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: hasResult ? C.accent : 'linear-gradient(135deg, #111827, #1B3A5C)', color: '#fff', fontSize: T.sm, fontWeight: 600, cursor: 'pointer' }}>
          {hasResult ? '✏️ 결과 수정' : '✏️ 결과 입력'}
        </button>
      </div>
    </>
  );

  if (panelMode) {
    return <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>{panelContent}</div>;
  }

  return (<>
    {showResultModal && (
      <ConsultResultModal consultation={c} templates={templates} taskCategories={taskCategories}
        aiPresets={aiPresets} fps={fps} wms={wms} existingTasks={tasks}
        onDone={handleResultDone} onCancel={() => setShowResultModal(false)}
        onToast={msg => { if (onToast) onToast(msg); }} />
    )}
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={close} style={{ position: 'absolute', inset: 0, background: 'rgba(17,24,39,0.4)', backdropFilter: 'blur(2px)' }} />
      <div style={{ position: 'relative', width: '100%', maxWidth: 560, maxHeight: '92vh', background: '#fff', borderRadius: 12, display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        {panelContent}
      </div>
    </div>
  </>);
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: T.xs, color: C.muted, fontWeight: 600, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: T.sm, fontWeight: 500 }}>{value}</div>
    </div>
  );
}
